import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Button } from "@/components/ui/button";
import { Download, Copy, Share2, Check } from "lucide-react";
import { LitraMark } from "@/components/Brand";
import api from "@/lib/api";
import { toast } from "sonner";

export const ShareCardComposer = ({ inputText, bindStatement, signals = [], archetype, reportId = "", watermark = true }) => {
  const ref = useRef(null);
  const [copied, setCopied] = useState(false);

  const cardText = `${inputText} — ${bindStatement}`;

  const recordShare = async () => {
    try { await api.post("/share/card", { report_id: reportId, card_text: cardText }); } catch (e) { /* ignore */ }
  };

  const download = async () => {
    if (!ref.current) return;
    try {
      const url = await toPng(ref.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.download = `litrametry-${inputText}.png`;
      a.href = url;
      a.click();
      await recordShare();
      toast.success("Share card downloaded");
    } catch (e) {
      toast.error("Could not render card");
    }
  };

  const copyText = async () => {
    await navigator.clipboard.writeText(cardText);
    setCopied(true);
    await recordShare();
    setTimeout(() => setCopied(false), 1500);
    toast.success("Copied card text");
  };

  return (
    <div data-testid="share-card-composer">
      <div
        ref={ref}
        data-testid="share-card-preview"
        className="rounded-2xl p-7 relative overflow-hidden"
        style={{
          background: "radial-gradient(700px 400px at 80% 0%, rgba(20,90,67,0.18), transparent 60%), #0B0C0E",
          border: "1px solid rgba(185,192,204,0.2)",
        }}
      >
        <div className="flex items-center gap-2 mb-6">
          <LitraMark size={22} />
          <span className="font-display font-semibold">Litrametry</span>
        </div>
        <div className="text-3xl font-display font-semibold tracking-tight">{inputText}</div>
        <div className="mt-2 text-[var(--bone-muted)]">{bindStatement}</div>
        <div className="mt-5 flex flex-wrap gap-2">
          {signals.slice(0, 3).map((s, i) => (
            <span key={i} className="mono-chip glass rounded-full px-3 py-1">{s}</span>
          ))}
        </div>
        {archetype && <div className="mt-5 text-sm gold-text">{archetype}</div>}
        {watermark && (
          <div className="absolute bottom-3 right-4 text-[10px] text-[var(--bone-faint)] mono-chip">litrametry.app</div>
        )}
      </div>
      <div className="flex gap-2 mt-3">
        <Button onClick={download} className="bg-[var(--primary)] hover:opacity-90" data-testid="share-card-download-button">
          <Download className="w-4 h-4 mr-2" /> Download
        </Button>
        <Button variant="outline" onClick={copyText} className="glass border-[rgba(185,192,204,0.25)]" data-testid="share-card-copy-text-button">
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />} Copy text
        </Button>
        <Button variant="outline" onClick={recordShare} className="glass border-[rgba(185,192,204,0.25)]" data-testid="share-card-share-button">
          <Share2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default ShareCardComposer;
